<template>
  <transition name="fade">
    <section :class="machine.state.value" class="uploaded-image-preview">
      <template v-if="isShowingFormState">
        <div class="row">
          <div class="img-wrapper">
            <img :src="image.url" />
          </div>
          <div class="image-description">
            <base-textarea
              ref="base-textarea"
              v-model="caption"
              :name="`${image.id}-caption`"
              :validation-rules="imageDescriptionValidationRules"
              :required="true"
              :rows="4"
              :should-focus="shouldFocus"
              @update:model-value="submit"
              @focus="onCaptionFocus"
              @click="onCaptionFocus"
              @blur="$bubble('caption-blur')"
            >
              <template #label>
                <translate class="field-label">Caption</translate>
              </template>
              <template #errorMessage="{ failedRules }">
                <translate v-if="failedRules.includes('required')">
                  Please add a photo caption
                </translate>
                <translate
                  v-else-if="failedRules.includes('max_value')"
                  :tt="{ max: MAX_CAPTION_LENGTH }"
                >
                  Please shorten this text to {max} characters or fewer
                </translate>
                <translate v-else> Please add a photo caption </translate>
              </template>
            </base-textarea>
          </div>
        </div>
        <translate tag="p" class="help-text">
          Add a clear description of your photo. By uploading a photo you
          confirm that you have the right to add it and it doesn't violate our
          terms and conditions.
        </translate>
      </template>
      <template v-else-if="isShowingImageInvalidState">
        <div class="row error">
          <div class="img-wrapper">
            <img :src="image.url" />
          </div>
          <div v-if="isInDimensionsInvalidState">
            <translate tag="p" class="help-text">
              This photo is too small and doesn't meet our quality guidelines,
              please upload the original. The minimum size for photos is 1800 x
              1350 pixels.
            </translate>
            <translate
              tag="p"
              class="help-text current-size"
              :tt="{ imageWidth, imageHeight }"
            >
              This image is: {imageWidth} x {imageHeight}
            </translate>
          </div>
          <div v-if="isInFileSizeInvalidState">
            <translate tag="p" class="help-text">
              This photo is too big
            </translate>
            <translate
              tag="p"
              class="help-text current-size"
              :tt="{ size: imageFileSizeInMB }"
            >
              This image is: { size }
            </translate>
          </div>
        </div>
      </template>
      <state-matches :state="machine.state">
        <template #removing>
          <div class="removing">
            <loading-spinner />
          </div>
        </template>
        <template #error>
          <div class="row">
            <div>
              <p class="help-text">An error occurred</p>
            </div>
          </div>
        </template>
      </state-matches>
      <delete-button-with-prompt
        :hash="image.id"
        class="delete"
        @delete="remove"
      >
        <translate>Are you sure you want to remove this photo?</translate>
      </delete-button-with-prompt>
    </section>
  </transition>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { UploadedImagePreviewStateMachineInterpreter } from '../machines/UploadedImagePreviewMachine'
import { ZodSchema } from 'zod'
import * as z from 'zod'
import { Form } from 'vee-validate'

@Component({
  components: { Form },
})
class UploadedImagePreview extends Vue {
  MAX_CAPTION_LENGTH = 100

  @Prop({ required: true })
  machine: UploadedImagePreviewStateMachineInterpreter

  @Prop({ default: false })
  shouldFocus: boolean

  imageDescriptionValidationRules: ZodSchema = z
    .string()
    .transform((value) => value.trim())
    .pipe(
      z
        .string({ required_error: 'required' })
        .min(1, { message: 'required' })
        .max(this.MAX_CAPTION_LENGTH, { message: 'max_value' }),
    )

  caption: string = ''

  hasReadFromMachineContext = false

  get image() {
    return this.machine.state.context.image
  }

  get imageWidth() {
    return this.image.width
  }

  get imageHeight() {
    return this.image.height
  }

  get imageFileSizeInMB() {
    return this.image.size / 1024 ** 2
  }

  get isShowingFormState() {
    return this.machine.state.hasTag('form')
  }

  get isShowingImageInvalidState() {
    return this.machine.state.hasTag('imageInvalid')
  }

  get isInDimensionsInvalidState() {
    return this.machine.state.hasTag('dimensionsInvalid')
  }

  get isInFileSizeInvalidState() {
    return this.machine.state.hasTag('fileSizeInvalid')
  }

  /**
   * check if caption input is pushed out of view by mobile browser
   * scroll/virtual keyboard
   * disconnect observer after real time wait to avoid creating multiple
   */
  async onCaptionFocus(event: any) {
    await new Promise((resolve) => setTimeout(resolve, 500))
    if (!('IntersectionObserver' in window)) return
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- DOM event target
    const selector = `[name="${event.target.name}"]`
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- DOM event target
    const element = event.target
    const captionSectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (!entry.isIntersecting || entry.intersectionRatio < 1) {
        this.$puScrollTo(selector, {
          duration: 1,
          offset: -200,
        })
      }
      captionSectionObserver.disconnect()
    })
    captionSectionObserver.observe(element)
    await new Promise((resolve) => setTimeout(resolve, 200))
    captionSectionObserver.disconnect()
  }

  remove() {
    this.machine.send('REMOVE_IMAGE')
  }

  mounted() {
    this.machine.onChange(({ caption }) => {
      // read value from machine context once only
      if (this.hasReadFromMachineContext) {
        return
      }
      this.caption = caption
      this.hasReadFromMachineContext = true
    })
  }

  submit(event: string | undefined = undefined) {
    if (event && event.length <= this.MAX_CAPTION_LENGTH) {
      this.machine.send('UPDATE_CAPTION', { caption: event })
    }
  }
}

export default toNative(UploadedImagePreview)
</script>
<style scoped>
section {
  border-radius: 0.2em;
  border: 2px solid var(--colour-table-borders);
  margin: 1em -0.5em;
  padding: 0.75em;
  position: relative;
}

.row {
  align-items: flex-start;
  justify-content: flex-start;
}

.img-wrapper {
  width: 6em;
  height: 6.5em;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex: 0 0 6em;
  margin: 0 0.5em 0 0;
}

img {
  width: auto;
  height: auto;
  max-height: 6em;
  max-width: 6em;
  vertical-align: top;
  margin: auto;
}

.image-description {
  flex-grow: 1;
  display: block;
}

.image-description :deep(label) {
  margin: 0;
  display: block;
}

.image-description :deep(textarea) {
  font-size: 16px; /* Fallback */
  font-size: clamp(16px, 1.24em, 2.5em);
}

.delete {
  align-items: center;
  background: white;
  border-radius: 50%;
  border: 2px solid var(--colour-table-borders);
  bottom: -0.4em;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  height: 2em;
  justify-content: center;
  margin: 0;
  padding: 0;
  position: absolute;
  right: -0.4em;
  width: 2em;
}

.uploaded-image-preview .help-text {
  font-size: var(--small);
}

.removing {
  text-align: center;
  padding: 1.4em;
}

.error {
  border-color: var(--colour-red);
}

.uploaded {
  border-color: var(--colour-mid-green);
}

.current-size {
  color: var(--colour-red);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

/* to give the delete icon a taller touch target so it can still be hit when the UI moves */
:deep(.delete-icon) {
  padding-top: 3em;
  position: relative;
  bottom: 1.5em;
}
</style>
